import { ApiError } from "@/lib/apiClient";

export function formatUploadLimitMb(maxBytes: number): number {
  return Math.round(maxBytes / (1024 * 1024));
}

function formatFileSizeMb(file: File): string {
  return (file.size / (1024 * 1024)).toFixed(1);
}

/** Maps HTTP upload failures (including nginx 413) to a clear user message. */
export function formatUploadHttpError(
  status: number,
  statusText: string,
  file: File,
  maxBytesPerFile?: number,
  apiMessage?: string,
): string {
  const limitMb = maxBytesPerFile ? formatUploadLimitMb(maxBytesPerFile) : 50;

  if (status === 400 && apiMessage) {
    return apiMessage;
  }

  if (
    status === 413 ||
    /entity too large|content too large|payload too large/i.test(statusText)
  ) {
    return `${file.name} (${formatFileSizeMb(file)} MB) is too large. Maximum is ${limitMb} MB per file.`;
  }

  if (status === 401) {
    return "Your session expired. Please sign in again and retry the upload.";
  }

  if (status === 429) {
    return "Too many uploads. Please wait a moment and try again.";
  }

  if (status >= 500) {
    return "The server could not accept this upload. Please try again in a few minutes.";
  }

  if (apiMessage) return apiMessage;
  if (statusText) return `Upload failed: ${statusText}`;
  return `Upload failed for ${file.name}.`;
}

export function formatUploadNetworkError(file: File): string {
  return `Could not upload ${file.name}. Check your connection. Large files may take longer — try again on a stable network.`;
}

/** Normalize any upload failure (network, 413, API validation) for toasts. */
export function formatUploadError(
  error: unknown,
  file: File,
  maxBytesPerFile?: number,
): string {
  if (error instanceof ApiError) {
    const maxBytes =
      typeof error.details?.maxBytes === "number"
        ? error.details.maxBytes
        : maxBytesPerFile;
    if (error.status === 0) {
      return error.message || formatUploadNetworkError(file);
    }
    return formatUploadHttpError(
      error.status,
      error.message,
      file,
      maxBytes,
      error.status === 400 ? error.message : undefined,
    );
  }

  if (error instanceof TypeError) {
    return formatUploadNetworkError(file);
  }

  if (error instanceof Error && error.message) {
    if (/failed to fetch|networkerror|load failed/i.test(error.message)) {
      return formatUploadNetworkError(file);
    }
    return error.message;
  }

  return `Upload failed for ${file.name}.`;
}
